/** The Graphics area and PRISM Graphics (0.92.0). */
import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

export interface GraphicsSettings {
  /** The PRISM Graphics editor's origin; null when PRISM_GRAPHICS_EDITOR_ORIGIN is not set. */
  editorOrigin: string | null;
  defaultHoldSeconds: number;
}

export interface GraphicSummary {
  resolution: { width: number; height: number } | null;
  frameRate: { num: number; den: number } | null;
  stepCount: number | null;
  fieldCount: number | null;
  /** 'ograf-v1' (PRISM Graphics), 'renderat' (an HTML template package), or null when not known yet. */
  engine: string | null;
  /** Made in PRISM Graphics, so the editor can open it. */
  editable: boolean;
}

export interface GraphicListItem extends GraphicSummary {
  assetId: string; assetCode: string; name: string; thumbnailUrl: string | null; currentVersion: number;
  status: 'processing' | 'ready' | 'failed'; error: { code: string; message: string } | null; updatedAt: string;
  /** Sequences with a clip of this graphic (0.95.0), for the delete confirmation. */
  usedInSequences: number;
}

export interface GraphicDetail extends Omit<GraphicSummary, 'engine'> {
  assetId: string; name: string; engine: string | null; currentVersion: number;
  versions: Array<{ version: number; status: 'processing' | 'ready' | 'failed'; savedBy: { id: string; displayName: string } | null; savedAt: string; metadata: Record<string, unknown> | null; error: { code: string; message: string } | null }>;
}

export function useGraphicsSettings() {
  return useQuery({ queryKey: queryKeys.graphicsSettings(), queryFn: () => api<GraphicsSettings>('/api/v1/graphics/settings'), staleTime: 5 * 60_000 });
}

export function useGraphics(params: { q?: string; collection?: string | null }) {
  const qs = new URLSearchParams();
  if (params.q?.trim()) qs.set('q', params.q.trim());
  if (params.collection) qs.set('collection', params.collection);
  const key = qs.toString();
  return useQuery({
    queryKey: queryKeys.graphics(key),
    queryFn: () => api<{ items: GraphicListItem[]; hasMore: boolean }>(`/api/v1/graphics/assets${key ? `?${key}` : ''}`),
    // A saved version is processed for a moment; keep the list current while any is.
    refetchInterval: (q) => (q.state.data?.items.some((i) => i.status === 'processing') ? 3000 : false),
  });
}

/** One graphic with its versions; `enabled` only for graphics assets. */
export function useGraphic(id: string | null | undefined, enabled = true) {
  return useQuery({ queryKey: queryKeys.graphic(id), queryFn: () => api<GraphicDetail>(`/api/v1/graphics/assets/${id}`), enabled: Boolean(id) && enabled });
}
